import React, { useRef, useEffect } from 'react'
import styled from 'styled-components'
import { Flex } from 'reflexbox/styled-components'
import { motion, useInView } from 'framer-motion'

import { PageRow } from '../global'

import NavBody from './body'
import NavFoot from './foot'
import NavHead from './head'

import footerGraphicLeft from '../../images/footer-graphic-left.png'
import footerGraphicRight from '../../images/footer-graphic-right.png'
import footerGraphicMobile from '../../images/footer-graphic-mobile.png'

const illustrationVariants = {
  left: {
    hidden: { opacity: 0, y: 80 },
    visible: (delay) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 1.1, delay, ease: [0.34, 1.56, 0.64, 1] },
    }),
  },
  right: {
    hidden: { opacity: 0, x: 80, y: -40 },
    visible: (delay) => ({
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration: 1.2, delay, ease: [0.34, 1.56, 0.64, 1] },
    }),
  },
}

export default ({ isFoot, onToggle, isToggled, location }) => {
  const footerRef = useRef(null)
  const inView = useInView(footerRef, { once: true, amount: 0.2 })

  let prevPath
  if (location) {
    prevPath = usePrevious(location.pathname)
  }

  useEffect(() => {
    if (location) {
      if (prevPath !== location.pathname) {
        if (typeof onToggle === 'function' && isToggled) {
          onToggle()
        }
      }
    }
  }, [location, prevPath, onToggle, isToggled])

  return (
    <Wrap isFoot={isFoot} isToggled={isToggled} ref={isFoot ? footerRef : null}>
      <PageRow fullHeight={true}>
        <LinksOuterWrap>
          {isFoot && (
            <>
              <Illustration
                className="illus-left"
                custom={0.1}
                variants={illustrationVariants.left}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
              >
                <img src={footerGraphicLeft} alt="" />
              </Illustration>
              <Illustration
                className="illus-right"
                custom={0.3}
                variants={illustrationVariants.right}
                initial="hidden"
                animate={inView ? 'visible' : 'hidden'}
              >
                <img src={footerGraphicRight} alt="" />
              </Illustration>
            </>
          )}
          <LinksOuter justifyContent="space-between" flexDirection="column">
            <NavHead
              isFoot={isFoot}
              onToggle={onToggle}
              isToggled={isToggled}
            />
            <NavBody />
            <MobileIllustration
              custom={0.2}
              variants={illustrationVariants.left}
              initial="hidden"
              animate={isFoot ? (inView ? 'visible' : 'hidden') : (isToggled ? 'visible' : 'hidden')}
            >
              <img src={footerGraphicMobile} alt="" />
            </MobileIllustration>
            {isFoot && <NavFoot />}
          </LinksOuter>
        </LinksOuterWrap>
      </PageRow>
    </Wrap>
  )
}

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const Wrap = styled.div`
  background-color: ${(props) => props.theme.pageTheme.bgSecondary};
  height: ${(props) => (props.isFoot ? '881px' : '100dvh')};
  width: 100%;
  padding-top: 3rem;
  position: ${(props) => (props.isFoot ? `relative` : `fixed`)};
  left: 0;
  top: 0;
  z-index: ${(props) => (props.isFoot ? `100` : `100`)};
  display: ${(props) =>
    props.isFoot ? `block` : props.isToggled ? `block` : `none`};
  overflow: ${(props) => (props.isFoot ? `visible` : `hidden`)};
  @media screen and (max-width: ${(props) => props.theme.breakpoints[0]}) {
    padding-top: 1.8rem;
    height: 100dvh;
    overflow-x: clip;
  }
`

const LinksOuterWrap = styled.div`
  position: relative;
  height: 100%;
`

const LinksOuter = styled(Flex)`
  height: 100%;
  position: relative;
  z-index: 1;
  @media screen and (max-width: ${(props) => props.theme.breakpoints[0]}) {
    justify-content: flex-start !important;
  }
`

const Illustration = styled(motion.div)`
  position: absolute;
  z-index: 0;
  pointer-events: none;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &.illus-left {
    width: 400px;
    bottom: 40px;
    left: -240px;
  }

  &.illus-right {
    width: 400px;
    top: 20px;
    right: -60px;

    @media screen and (max-width: 1440px) {
      right: -5vw;
    }

    @media screen and (max-width: 1420px) {
      right: -20vw;
    }

    @media screen and (max-width: 1100px) {
      right: -30vw;
    }
  }

  @media screen and (max-width: ${(props) => props.theme.breakpoints[0]}) {
    display: none;
  }
`

const MobileIllustration = styled(motion.div)`
  display: none;
  pointer-events: none;
  z-index: 0;

  position: absolute;

  bottom: 10vh;
  left: -20vw;
  img {
    width: 120vw;
    height: auto;
  }

  z-index: -1;

  @media screen and (max-width: ${(props) => props.theme.breakpoints[0]}) {
    display: block;
    width: 150%;
    margin: 0 auto;
    padding: 0 1rem;
  }
`
